// import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import _ from 'lodash'
import '../styles/landingPage.css'
import { setActiveArticle } from '../slices/coreAssetsSlice'
import { AuthStdFooter } from "../utils/auth-std-footer";

import ArticleSlider from '../components/aaiArticleSidebar'
import { ContentFetcher } from '../utils/contentFetcher';
import BackNextButtonContainer from '../utils/backNextButtonContainer';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { config } from '../constants/config';

/**
 * The post-meta line used to hard-code "May 2, 2023" and "Uncategorized" for every article. Both
 * facts are on the node: `createdAt`, `updatedOn` and `categories`.
 *
 * ⭐ `updatedOn` is preferred over `createdAt`, because these are living documents — one article in
 * the set was created 2026-01-18 and last revised 2026-09-11, and the later date is the honest one
 * to show a reader.
 *
 * ⚠️ The server already runs `convertNeo4jDateToJSDate` before returning an article, so this
 * receives something Date can parse. It is still guarded: the raw Neo4j temporal is a nested object
 * of `{low, high}` Integer pairs, and if that ever reaches here unconverted, `new Date()` yields
 * Invalid Date and would render the literal string "Invalid Date" to a reader. Returning empty is
 * the better failure.
 */
function formatPublished(article) {
  const raw = article?.updatedOn || article?.createdAt;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/** `categories` is an ARRAY on the node (e.g. ['tutorial']), not a string. */
function formatCategories(categories) {
  if (!Array.isArray(categories) || !categories.length) return 'Uncategorized';
  return categories
    .filter(Boolean)
    .map((c) => String(c).replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()))
    .join(', ');
}

export default function ArticleItem() {
  const dispatch = useDispatch()
  let curArticleItem = useRef(null)
  let nextPrev = useRef({ first: 0, prev: 0, next: 0, last: 0 })
  const activeArticleUuid = useSelector((state) => state.core.activeArticle);

  /**
   * ⭐⭐ THE ROUTE PARAM IS NOW READ. The route has always been /articleItemPage/:id and this page
   * has always ignored it, resolving the article from the redux LIST via activeArticle instead —
   * which works only when the reader arrived by clicking a card.
   *
   * ⛔ Opening the URL directly fell through to `articles[0]`: a WRONG article, rendered with no
   * indication that it was wrong. That is the failure mode for any article the public list does not
   * carry — an unlisted page linked from an email is exactly that case.
   *
   * ⓘ So: if the uuid is in the list, the list path runs unchanged and back/next still work. If it
   * is not, the article is fetched on its own by uuid (get-articleitem is container-agnostic and a
   * public GET) and rendered WITHOUT navigation, because it belongs to no sequence.
   *
   * ⚠️ Deliberately local state, not the `fetchArticle` thunk: that thunk's fulfilled case assigns
   * the single article over `state.articles`, which would leave `state.articles.articles` undefined
   * and break every other consumer of the list.
   */
  const { id } = useParams();

  /**
   * ⭐ A WAY BACK, WHEN THE CALLER SAID WHERE IT CAME FROM. `/guide/:id?from=/listen` is how the
   * downloads page links its instructions; without this a reader lands on a bare article with no
   * navbar — by design — and no route onward either, which is a dead end on a phone.
   *
   * ⛔ RELATIVE PATHS ONLY, AND THAT IS A SECURITY CHECK RATHER THAN TIDINESS. `?from=` arrives in
   * a URL anybody can write, so an unchecked value turns this page into an open redirector:
   * `?from=https://evil.example` would render OUR page with a link somebody else chose. One leading
   * slash and no second one — `//host` is protocol-relative and leaves the site just as surely as
   * `https://` does.
   *
   * ⓘ No `from`, no link. An article reached from the Info archive has the navbar for that.
   */
  const [search] = useSearchParams();
  const fromRaw = search.get('from') || '';
  const backTo = /^\/[^/]/.test(fromRaw) ? fromRaw : null;
  const [standalone, setStandalone] = useState(null);
  const listHasId = useSelector((state) => {
    const list = state.core && state.core.articles && state.core.articles.articles;
    if (!id || !Array.isArray(list)) return false;
    return list.some((a) => a && a.uuid === id);
  });
  const isStandalone = !!id && !listHasId;

  /**
   * ⛔ THIS SYNCS THE ROUTE TO THE ACTIVE ARTICLE ONCE, AND MUST NOT WATCH IT AFTERWARDS.
   *
   * ⚠️ My first version depended on `activeArticleUuid` and re-asserted the route id whenever they
   * differed. Back and next change the active article BY DESIGN, so every click was immediately
   * undone: the page snapped back to whichever article the URL named, and the hero image — the
   * most visible thing on it — appeared frozen.
   *
   * ⓘ So the dependency list is deliberately (id, listHasId): the route id is applied when it
   * changes, or when the list finally contains it, and navigation is then left alone.
   */
  useEffect(() => {
    if (!id) return undefined;
    if (listHasId) {
      dispatch(setActiveArticle(id));
      setStandalone(null);
      return undefined;
    }
    let cancelled = false;
    axios.get(config.GET_API_ARTICLE, { params: { uuid: id } })
      .then((r) => { if (!cancelled) setStandalone((r.data && r.data.article) || false); })
      .catch(() => { if (!cancelled) setStandalone(false); });
    return () => { cancelled = true; };
  }, [id, listHasId, dispatch]);

  const setNextPrev = (articles, index) => {
    let lastItem = articles.length - 1
    nextPrev.current.last = nextPrev.current.last = articles[lastItem] ? articles[lastItem].uuid : null;
    nextPrev.current.first = nextPrev.current.first = articles[0] ? articles[0].uuid : null;

    nextPrev.current.next = nextPrev.current.next = articles[index + 1] ? articles[index + 1].uuid : null;
    nextPrev.current.prev = nextPrev.current.prev = articles[index - 1] ? articles[index - 1].uuid : null;
    if (nextPrev.current.next !== null) if (index + 1 > articles.length) nextPrev.current.next = 0
    if (nextPrev.current.prev !== null) if (index - 1 < 0) nextPrev.current.prev = articles[0].uuid
    // }
    console.log('current idx', index, ' nextPrev.current', nextPrev.current)
    return articles[0]
  }


  useSelector((state) => {
    let foundArticle = null;
    // ⛔ No articles[0] fallback for a uuid the list does not hold — that fallback IS the bug.
    if (isStandalone) return null;
    if (state.core && state.core.articles && state.core.articles.articles && activeArticleUuid) {
      foundArticle = _.find(state.core.articles.articles, (x, index) => {
        if (x.uuid === activeArticleUuid) {
          foundArticle = x
          setNextPrev(state.core.articles.articles, index)
          curArticleItem.current = foundArticle
          return x;
        }
      })
      if (foundArticle !== -1) return foundArticle
    }
    if (foundArticle === null && state.core && state.core.articles && state.core.articles.articles) {
      foundArticle = state.core.articles.articles[0]
      setNextPrev(state.core.articles.articles, 0)
      curArticleItem.current = foundArticle
      return foundArticle
    }
    return null;
  });


  const handleFirstClick = () => {
    console.log('First button clicked', nextPrev.current.first);
    // add your code here
    if (nextPrev.current.first) dispatch(setActiveArticle(nextPrev.current.first))

  };

  const handleBackClick = () => {
    console.log('Back button clicked', nextPrev.current.prev);
    // add your code here
    if (nextPrev.current.prev) dispatch(setActiveArticle(nextPrev.current.prev))
  };

  const handleNextClick = () => {
    console.log('Next button clicked', nextPrev.current.next);
    // add your code here
    if (nextPrev.current.next) dispatch(setActiveArticle(nextPrev.current.next))

  };

  const handleLastClick = () => {
    console.log('Next button clicked', nextPrev.current.next);
    // add your code here
    if (nextPrev.current.last) dispatch(setActiveArticle(nextPrev.current.last))

  };

  // const [activeArticleItem, setActiveArticleItem] = useState({})
  // useEffect(() => {
  //   if (activeArticleUuid) {
  //     let curItem = curArticleItem
  //     console.log('Draw this ArticleItem curItem', curItem)

  //     // setActiveArticleItem(getArticleByUuid(activeArticleUuid))
  //   }
  // }, [activeArticleUuid])
  const articleItem = (item) => {
    return (
      <div id="et-main-area" style={{ marginBottom: "70px" }}>
        <div id="main-content">

          {/* ⓘ `et_no_sidebar` is the stylesheet's own convention for a one-column article
              (.et_no_sidebar #left-area { width: 100% !important }), so the left column takes the
              whole width instead of leaving the sidebar's 30% empty. */}
          <div className={'article-container' + (isStandalone ? ' et_no_sidebar' : '')}>
            {/* ⓘ First/back/next/last walk the fetched LIST. A standalone article is not in that
                list, so the buttons would navigate away from it into articles[0] — which is the
                very confusion this page used to create silently. */}
            {!isStandalone && (
              <div style={{ width: "600px" }} >
                <BackNextButtonContainer
                  onToFirstClick={handleFirstClick}
                  onBackClick={handleBackClick}
                  onNextClick={handleNextClick}
                  onToLastClick={handleLastClick} />
              </div>
            )}
            <div id="content-area" className="clearfix">

              <div id="left-area">
                <article id="post-413" className="et_pb_post post-413 post type-post status-publish format-standard has-post-thumbnail hentry">
                  <div className="et_post_meta_wrapper">
                    <div className="et_post_meta_container">
                      {/* ⓘ Above the title, where a back affordance is looked for, and only when the
                          caller asked for one. */}
                      {backTo && (
                        <p className="article-back">
                          <Link to={backTo}>‹ Back to your books</Link>
                        </p>
                      )}
                      <h1 className="entry-title">{item.name}</h1>
                      <p className="post-meta"> by <span className="author vcard">
                        {/* ⚠️ A react-router <Link>, not an <a href="/about">. An anchor to an
                          internal route forces a full page reload, which drops the SPA state and
                          re-fetches the whole bundle. The previous value here was worse still — a
                          hard-coded link to https://awethorator.com/author/authorboss/, a different
                          domain, unrelated to whichever author the article actually names. */}
                        <Link to="/about" title="About AuthoratorAI" rel="author">{item.author}</Link>
                      </span> | <span className="published">{formatPublished(item)}</span>
                        {' '}| {formatCategories(item.categories)}
                      </p>
                    </div>
                    {/* <img src={item.heroImageUrl} alt="" className="hero-image" width="1080" /> */}
                    {/* ⚠️ An article may legitimately have no hero — the sample-excerpt page does.
                        An empty src is not nothing: the browser resolves "" against the page URL and
                        re-requests the document as an image. */}
                    {item.heroImageUrl ? (
                      <div className="hero-container-a">
                        <img className='hero-centered-image-a' src={item.heroImageUrl} alt="" />
                      </div>
                    ) : null}
                    {/* <img className='hero-image-a' src={item.heroImageUrl} alt="" /> */}

                  </div>
                  <div className="entry-content">
                    {/* <figure className="wp-block-image size-large is-resized">
                      <img decoding="async" src={item.thumbnailImageUrl} alt="" className="wp-image-194" width="256" height="256" />
                    </figure> */}
                    <div>{<ContentFetcher url={item.contentUrl} />}</div>
                  </div>
                  <div className="et_post_meta_wrapper">
                  </div>
                </article>
              </div>
              {/* ⛔ NO INFO ARCHIVE ON A STANDALONE PAGE. The tree is built from the fetched
                  article LIST, and a page reached by its own uuid has no list — so the column drew
                  its heading and professor over an empty tree. ⓘ It is also the wrong offer: a
                  reader arriving from an email to hear a sample has not come to browse the
                  archive, and the page should not imply a navigation that is not there. */}
              {!isStandalone && (
                <div id="right-area" className="article-right-column" >
                  <ArticleSlider />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
  const item = isStandalone ? standalone : curArticleItem.current;
  if (item) return (<>{articleItem(item)}</>)

  // ⚠️ `false` means the fetch came back empty: a uuid that is not an article, or one that has
  // been deleted. A reader following an old link gets a sentence, not a perpetual "Loading...".
  if (standalone === false) return (
    <div style={{ padding: '3rem', textAlign: 'center' }}>That page is not available.</div>
  )
  return (<div>Loading...</div>)
}